import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { PrimaryButton, SectionLink } from './MarketingButtons';
import { DodoLogo, DodoMark } from './Logo';
import { curriculumStrands } from './marketingContent';
import { FONT_BODY, FONT_DISPLAY } from './marketingStyles';

// The public site's header and footer, shared by the landing page and the
// footer pages (Privacy, Terms, Contact, Accessibility, Safeguarding) so
// every public page has the same navigation.

export function MarketingHeader() {
  // Phone-only menu for the section links (on a phone they used to simply
  // disappear, and the header wrapped onto two rows).
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => {
    setMenuOpen(false);
  };
  return (
    <>
      {/* Sticky header lives at the top level, outside any
          overflow-hidden/clip wrapper -- nested inside one, `position:
          sticky` only sticks within that ancestor's own box, so the header
          would vanish the moment a visitor scrolled past the hero instead
          of staying reachable for the whole page. */}
      <div className="sticky top-0 z-50 w-full border-b border-line bg-paper/95 px-4 py-3 backdrop-blur-sm sm:px-6 lg:px-20">
        <div className="mx-auto flex min-h-14 w-full max-w-[1280px] items-center justify-between gap-4 lg:min-h-[72px]">
          <Link
            to="/"
            aria-label="DodoSkills Maths — home"
            className="flex items-center"
          >
            <DodoLogo />
          </Link>
          {/* Redesign 2026-09-30: four short section links. Audience links
            (parents / teachers / schools) now live in the hero's four
            doors and the footer, so the header doesn't repeat them. */}
          <nav
            aria-label="Marketing navigation"
            className="hidden flex-wrap gap-7 xl:flex"
          >
            <SectionLink targetId="how-it-works">How it works</SectionLink>
            <SectionLink targetId="curriculum">Curriculum</SectionLink>
            <SectionLink targetId="standards">Why no games</SectionLink>
            <Link
              to="/guide"
              className={`${FONT_BODY} text-[14px] font-medium whitespace-nowrap text-ink-soft`}
            >
              Guides
            </Link>
          </nav>
          <div className="flex items-center gap-3 sm:gap-[18px]">
            <Link
              to="/learn"
              className={`${FONT_BODY} hidden min-h-11 items-center text-[14px] font-medium whitespace-nowrap text-ink lg:inline-flex`}
            >
              Student login
            </Link>
            <Link
              to="/auth/sign-in"
              className={`${FONT_BODY} inline-flex min-h-11 items-center text-[14px] font-medium whitespace-nowrap text-ink`}
            >
              Log in
            </Link>
            <span className="hidden sm:contents">
              <PrimaryButton to="/auth/sign-up">Get Started Free</PrimaryButton>
            </span>
            <button
              type="button"
              aria-expanded={menuOpen}
              aria-controls="landing-menu"
              onClick={() => {
                setMenuOpen((open) => !open);
              }}
              className="flex min-h-10 items-center gap-2 rounded-lg border border-line bg-white px-3 text-sm font-semibold text-forest-950 xl:hidden"
            >
              <span aria-hidden="true">{menuOpen ? '✕' : '☰'}</span> Menu
            </button>
          </div>
        </div>
        {menuOpen && (
          <nav
            id="landing-menu"
            aria-label="Marketing navigation (menu)"
            className="absolute inset-x-0 top-full flex flex-col gap-1 border-b border-line bg-paper px-4 pt-2 pb-5 shadow-card sm:px-6 xl:hidden"
          >
            <SectionLink
              targetId="how-it-works"
              className="min-h-11 text-[16px] text-ink"
              onNavigate={closeMenu}
            >
              How it works
            </SectionLink>
            <SectionLink
              targetId="curriculum"
              className="min-h-11 text-[16px] text-ink"
              onNavigate={closeMenu}
            >
              Curriculum
            </SectionLink>
            <SectionLink
              targetId="standards"
              className="min-h-11 text-[16px] text-ink"
              onNavigate={closeMenu}
            >
              Why no games
            </SectionLink>
            <Link
              to="/guide"
              onClick={closeMenu}
              className="flex min-h-11 items-center text-[16px] font-medium text-ink"
            >
              Guides
            </Link>
            <Link
              to="/learn"
              onClick={closeMenu}
              className="flex min-h-11 items-center text-[16px] font-medium text-ink"
            >
              Student login
            </Link>
            <div className="mt-2 sm:hidden">
              <PrimaryButton to="/auth/sign-up">Get Started Free</PrimaryButton>
            </div>
          </nav>
        )}
      </div>
    </>
  );
}

// Footer links: 44px tall tap areas on a phone (they were 20px, packed
// tightly), back to the compact list on larger screens.
const FOOTER_LINK =
  'inline-flex min-h-11 items-center text-[14px] font-medium whitespace-nowrap text-forest-800 underline-offset-4 hover:underline sm:min-h-0 sm:text-[13px]';

export function MarketingFooter() {
  return (
    <div className="w-full border-t border-line px-5 py-10 sm:px-6 lg:px-20 lg:py-12">
      <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-10">
        <div className="flex w-full flex-wrap items-start justify-between gap-10">
          <div className="flex w-full max-w-[340px] flex-col gap-3">
            <span className="flex items-center gap-2.5">
              <DodoMark size={36} subject="umbrella" />
              <span
                className={`${FONT_DISPLAY} text-[24px] font-bold text-ink`}
              >
                DodoSkills
              </span>
            </span>
            <p className="text-[14px] leading-[1.5] text-ink-soft">
              Calm, curriculum-mapped practice for Australian students. Maths
              for Years 1–8 available now. More subjects coming.
            </p>
          </div>
          <div className="flex w-[160px] flex-col gap-1 sm:gap-3">
            <p className={`${FONT_DISPLAY} text-[13px] font-bold text-ink`}>
              Maths
            </p>
            <SectionLink targetId="curriculum" className={FOOTER_LINK}>
              Curriculum
            </SectionLink>
            <Link to="/auth/sign-up?role=parent_carer" className={FOOTER_LINK}>
              For Parents
            </Link>
            <Link to="/auth/sign-up?role=teacher" className={FOOTER_LINK}>
              For Teachers
            </Link>
            <Link to="/learn" className={FOOTER_LINK}>
              Student login
            </Link>
            <SectionLink targetId="standards" className={FOOTER_LINK}>
              Why No Games
            </SectionLink>
          </div>
          <div className="flex w-[200px] flex-col gap-1 sm:gap-3">
            <p className={`${FONT_DISPLAY} text-[13px] font-bold text-ink`}>
              Curriculum
            </p>
            {/* All six strands by name (it listed three combined names, which
                read as if there were only three topics). Each links to the
                curriculum section; the detail is inside, after log in. */}
            <SectionLink
              targetId="curriculum"
              className={`${FOOTER_LINK} font-semibold`}
            >
              All 6 strands · Years 1–8
            </SectionLink>
            {curriculumStrands.map((strand) => (
              <SectionLink
                key={strand}
                targetId="curriculum"
                className={FOOTER_LINK}
              >
                {strand}
              </SectionLink>
            ))}
          </div>
          <div className="flex w-[160px] flex-col gap-1 sm:gap-3">
            <p className={`${FONT_DISPLAY} text-[13px] font-bold text-ink`}>
              Company
            </p>
            <SectionLink targetId="standards" className={FOOTER_LINK}>
              Our Approach
            </SectionLink>
            <Link to="/guide" className={FOOTER_LINK}>
              How-to guides
            </Link>
            <Link to="/auth/sign-up?role=school" className={FOOTER_LINK}>
              For Schools
            </Link>
            <Link to="/contact" className={FOOTER_LINK}>
              Contact
            </Link>
            <Link to="/terms" className={FOOTER_LINK}>
              Privacy & Terms
            </Link>
          </div>
        </div>
        <div className="h-px w-full bg-line" />
        <div className="flex w-full flex-col gap-2 text-[13px] text-muted lg:flex-row lg:items-start lg:justify-between">
          <p>© 2026 DodoSkills. Learning without the noise.</p>
          <nav aria-label="Legal" className="flex flex-wrap gap-x-4 gap-y-1">
            <Link
              to="/privacy"
              className="font-medium text-forest-800 underline-offset-4 hover:underline"
            >
              Privacy
            </Link>
            <span aria-hidden="true">·</span>
            <Link
              to="/terms"
              className="font-medium text-forest-800 underline-offset-4 hover:underline"
            >
              Terms
            </Link>
            <span aria-hidden="true">·</span>
            <Link
              to="/accessibility"
              className="font-medium text-forest-800 underline-offset-4 hover:underline"
            >
              Accessibility
            </Link>
            <span aria-hidden="true">·</span>
            <Link
              to="/safeguarding"
              className="font-medium text-forest-800 underline-offset-4 hover:underline"
            >
              Safeguarding
            </Link>
          </nav>
        </div>
      </div>
    </div>
  );
}

// Frame for a public footer page: shared header, a readable-width column
// with the page title, and the shared footer.
export function MarketingPage({
  title,
  intro,
  children,
}: {
  title: string;
  intro?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div
      className={`${FONT_BODY} dm-workspace flex min-h-screen w-full flex-col bg-paper`}
    >
      <MarketingHeader />
      <main
        id="main-content"
        tabIndex={-1}
        className="mx-auto w-full max-w-3xl flex-1 px-4 py-10 sm:px-6 lg:py-14"
      >
        <h1
          className={`${FONT_DISPLAY} text-[34px] leading-tight font-bold text-ink sm:text-[40px]`}
        >
          {title}
        </h1>
        {intro && <div className="mt-3 text-[17px] text-ink-soft">{intro}</div>}
        <div className="mt-8">{children}</div>
      </main>
      <div className="w-full bg-white">
        <MarketingFooter />
      </div>
    </div>
  );
}
